import { useCallback, useMemo, useState } from 'react'
import type { IFilterDataArray } from '@rankmi/rankmi-table/dist/interfaces/filterProps'
import { Button } from 'react-bootstrap'
import { useFieldArray } from 'react-hook-form'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus'
import { UserAttributes } from 'api/administration/participants/types'
import AddParticipantsModal from 'components/configuration/components/lms-profiles/AddParticipantsModal'
import { ParticipantsActions } from 'pages/configuration/LmsProfiles/components/participants'
import { useTranslationRankmi } from 'helpers/translations.helper'
import { prefixParticipants } from 'pages/configuration/LmsProfiles/constants'
import { useCreateProfile } from '../../../../context'
import Table from './components/Table'
import { Wrapper, HeaderActions } from './styled'

export const Participants = () => {
  const [open, setOpen] = useState(false)

  const { control } = useCreateProfile()

  const { fields, append, remove, replace } = useFieldArray({
    name: 'users',
    control,
  })

  const users = useMemo(() => fields.map((field) => field.userId), [fields])

  const existingUserIds = useMemo(() => new Set(users), [users])

  const onAddUsers = useCallback(
    (newUsers: UserAttributes[]) => {
      const toAppend = newUsers
        .map((u) => u.id)
        .filter((id) => !existingUserIds.has(id))
      if (!toAppend.length) return
      append(toAppend.map((userId) => ({ userId })))
    },
    [append, existingUserIds]
  )

  const removeByUserId = useCallback(
    (userId: number) => {
      const idx = fields.findIndex((f) => f.userId === userId)
      if (idx >= 0) remove(idx)
    },
    [fields, remove]
  )

  const filter = useMemo(
    (): IFilterDataArray[] => [
      {
        input: 'id',
        condition: 'and',
        operator: 'not_in',
        // @ts-ignore TODO: fix this in @rankmi/rankmi-table
        compareTo: users,
      },
    ],
    [users]
  )

  const tRelative = useTranslationRankmi({
    prefix: prefixParticipants,
  })

  const [selectedUsers, setSelectedUsers] = useState<number[]>([])

  return (
    <div className="mt-3">
      <AddParticipantsModal
        show={open}
        onClose={() => setOpen(false)}
        onConfirm={(newUsers) => {
          onAddUsers(newUsers)
          setOpen(false)
        }}
        filter={filter}
      />
      <Wrapper>
        <Table
          users={users}
          onRemove={removeByUserId}
          selected={selectedUsers}
          setSelected={setSelectedUsers}
          actions={
            <HeaderActions className="d-flex justify-content-end">
              <Button size="sm" onClick={() => setOpen(true)} className="mr-2">
                <FontAwesomeIcon icon={faPlus} />
                {tRelative('addParticipants')}
              </Button>
              <ParticipantsActions
                selected={selectedUsers.length}
                onRemoveSelected={() => {
                  const selectedIds = new Set(selectedUsers)
                  replace(
                    fields.filter(({ userId }) => !selectedIds.has(userId))
                  )
                  setSelectedUsers([])
                }}
              />
            </HeaderActions>
          }
        />
      </Wrapper>
    </div>
  )
}
